import { createFileRoute } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { CONTACT, contactEmail } from '../content'

export const Route = createFileRoute('/contact')({
  head: () => ({
    meta: [
      { title: 'contact - jfrader' },
      { name: 'description', content: 'contact jfrader.' },
      { property: 'og:title', content: 'contact - jfrader' },
      { property: 'og:description', content: 'contact jfrader.' },
      { property: 'og:url', content: 'https://jfrader.com/contact' },
    ],
    links: [
      { rel: 'canonical', href: 'https://jfrader.com/contact' }
    ]
  }),
  component: ContactPage,
})

type SendState = 'idle' | 'sending' | 'sent' | 'error'

function ContactPage() {
  const [showEmail, setShowEmail] = useState(false)
  const [sendState, setSendState] = useState<SendState>('idle')
  const [sendError, setSendError] = useState('')

  async function sendMessage(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    setSendState('sending')
    setSendError('')

    try {
      const response = await fetch('/api/v1/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.get('name'),
          email: data.get('email'),
          message: data.get('message'),
          website: data.get('website'),
        }),
      })

      if (response.status === 429) throw new Error('too many messages, try again later')
      if (!response.ok) throw new Error('could not send, try again')

      form.reset()
      setSendState('sent')
    } catch (error) {
      setSendError(error instanceof Error ? error.message : 'could not send, try again')
      setSendState('error')
    }
  }

  return (
    <>
      <section className="sec">
        <h2 className="sec__label">contact</h2>
        <div className="contact">
          <div className="crow">
            <b>email</b>
            {showEmail ? (
              <a href={`mailto:${contactEmail()}`}>{contactEmail()}</a>
            ) : (
              <button type="button" className="reveal" onClick={() => setShowEmail(true)}>
                reveal address
              </button>
            )}
          </div>
          {CONTACT.map(([key, label, href]) => (
            <div className="crow" key={key}>
              <b>{key}</b>
              <a href={href} {...(href.startsWith('http') ? { target: '_blank', rel: 'noreferrer' } : {})}>
                {label}
              </a>
            </div>
          ))}
        </div>

        <form className="form" onSubmit={sendMessage}>
          <div className="field">
            <label htmlFor="contact-name">name</label>
            <input id="contact-name" name="name" type="text" autoComplete="name" maxLength={120} />
          </div>
          <div className="field">
            <label htmlFor="contact-email">email</label>
            <input id="contact-email" name="email" type="email" autoComplete="email" maxLength={254} />
          </div>
          <div className="field">
            <label htmlFor="contact-message">message</label>
            <textarea id="contact-message" name="message" rows={6} maxLength={4000} required />
          </div>
          <input className="hp" type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <div className="form__actions">
            <button className="pay__btn pay__btn--accent" type="submit" disabled={sendState === 'sending'}>
              {sendState === 'sending' ? 'sending' : 'send'}
            </button>
            {sendState === 'sent' && (
              <span className="form__status form__status--ok" role="status">sent</span>
            )}
            {sendState === 'error' && (
              <span className="form__status form__status--err" role="status">{sendError}</span>
            )}
          </div>
        </form>
      </section>
    </>
  )
}
